import React, { useEffect, useState } from 'react';
import { Button, Card, Dropdown, DropdownButton, Form } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import { CompanyService } from '../CompanyService';
import { ErrorToast, SuccessToast } from '../../../utils/toasters';
import { Company } from '../../../model/company/Company';
import { CreateGroupOptionModal } from './CreateGroupOptionModal';
import { EditGroupOptionModal } from './EditGroupOptionModal';
import { CreateGroupModal } from './CreateGroupModal';
import { EditGroupModal } from './EditGroupModal';
import { hasAccessRight } from '../../../utils/Functions';
import { AdminAclEnum } from '../../../enum/AdminAclEnum';
import generalSlice from '../../../store/general/GeneralSlice';
import { useDispatch } from 'react-redux';

interface IGroupOption {
    id: number;
    label: string;
    labelDe?: string;
    position?: number;
}

interface ICompanyUserGroup {
    id: number;
    name: string;
    nameDe?: string;
    isMultiple: boolean;
    isActive: boolean;
    supplier?: { id: number; displayName: string };
    options?: IGroupOption[];
}

export function CompanyGroups() {
    const [groups, setGroups] = useState<ICompanyUserGroup[]>([]);
    const { id }: any = useParams();
    const dispatch = useDispatch();
    const [company, setCompany] = useState<Company | null>(null);
    const [optionModal, setOptionModal] = useState<{ show: boolean; groupId: number }>({ show: false, groupId: 0 });
    const [editOptionModal, setEditOptionModal] = useState<{
        show: boolean;
        groupId: number;
        option: IGroupOption | null;
    }>({
        show: false,
        groupId: 0,
        option: null,
    });
    const [groupModal, setGroupModal] = useState<{ show: boolean; group: ICompanyUserGroup | null }>({
        show: false,
        group: null,
    });
    const [supplierFilter, setSupplierFilter] = useState<number | ''>('');
    const [suppliers, setSuppliers] = useState<{ id: number; displayName: string }[]>([]);
    const canManageActions = hasAccessRight(AdminAclEnum.COMPANY_GROUPS_EDIT);

    useEffect(() => {
        CompanyService.getClientSuppliers(Number(id))
            .then(data => setSuppliers(data.data ?? data))
            .catch(err => ErrorToast(err));
    }, []);

    useEffect(() => {
        Promise.all([CompanyService.getCompanyGroups(id), CompanyService.getCompany(id)])
            .then(([groupsData, companyData]) => {
                setGroups(groupsData.data ?? groupsData);
                setCompany(companyData);
            })
            .catch(err => ErrorToast(err));
    }, []);

    // const handleDeleteGroup = (groupId: number) => {
    //     CompanyService.deleteGroup(groupId)
    //         .then(() => setGroups(prev => prev.filter(g => g.id !== groupId)))
    //         .catch(err => ErrorToast(err));
    // };
    //
    // const handleDeleteOption = (groupId: number, optionId: number) => {
    //     CompanyService.deleteGroupOption(groupId, optionId)
    //         .then(() =>
    //             setGroups(prev =>
    //                 prev.map(g => (g.id === groupId ? { ...g, options: g.options?.filter(o => o.id !== optionId) } : g))
    //             )
    //         )
    //         .catch(err => ErrorToast(err));
    // };
    const handleDeleteGroup = (groupId: number) => {
        CompanyService.deleteGroup(groupId)
            .then(() => {
                setGroups(prev => prev.filter(g => g.id !== groupId));
                dispatch(
                    generalSlice.actions.setConfirmModal({
                        show: false,
                    })
                );
                SuccessToast('Successfully deleted');
            })
            .catch(err => ErrorToast(err));
    };

    const handleDeleteOption = (groupId: number, optionId: number) => {
        CompanyService.deleteGroupOption(groupId, optionId)
            .then(() => {
                setGroups(prev =>
                    prev.map(g => (g.id === groupId ? { ...g, options: g.options?.filter(o => o.id !== optionId) } : g))
                );
                dispatch(
                    generalSlice.actions.setConfirmModal({
                        show: false,
                    })
                );
                SuccessToast('Successfully deleted');
            })
            .catch(err => ErrorToast(err));
    };

    const handleOptionCreated = (groupId: number, option: any) => {
        setGroups(prev => prev.map(g => (g.id === groupId ? { ...g, options: [...(g.options ?? []), option] } : g)));
    };

    const handleOptionUpdated = (groupId: number, updated: IGroupOption) => {
        setGroups(prev =>
            prev.map(g =>
                g.id === groupId ? { ...g, options: g.options?.map(o => (o.id === updated.id ? updated : o)) } : g
            )
        );
    };

    const handleGroupCreated = (group: ICompanyUserGroup) => {
        setGroups(prev => [...prev, { ...group, options: [] }]);
    };

    const handleGroupUpdated = (updated: ICompanyUserGroup) => {
        setGroups(prev => prev.map(g => (g.id === updated.id ? { ...g, ...updated } : g)));
    };

    const filteredGroups = supplierFilter ? groups.filter(g => g.supplier?.id === supplierFilter) : groups;

    return (
        <>
            <CreateGroupOptionModal
                show={optionModal.show}
                groupId={optionModal.groupId}
                onHide={() => setOptionModal({ show: false, groupId: 0 })}
                onCreated={handleOptionCreated}
            />
            <EditGroupOptionModal
                show={editOptionModal.show}
                groupId={editOptionModal.groupId}
                option={editOptionModal.option}
                onHide={() => setEditOptionModal({ show: false, groupId: 0, option: null })}
                onUpdated={handleOptionUpdated}
            />
            <CreateGroupModal
                show={!groupModal.group && groupModal.show}
                clientId={Number(id)}
                clientName={company?.displayName ? company?.displayName : ''}
                onHide={() => setGroupModal({ show: false, group: null })}
                onCreated={handleGroupCreated}
            />
            <EditGroupModal
                show={!!groupModal.group && groupModal.show}
                clientId={Number(id)}
                group={groupModal.group}
                onHide={() => setGroupModal({ show: false, group: null })}
                onUpdated={handleGroupUpdated}
            />
            <Card className="mb-5 p-4">
                {/* Header */}
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <h5 className="mb-0">{company?.displayName ?? company?.displayName}</h5>
                    <div className="mb-3 d-flex align-items-center gap-2">
                        <Form.Select
                            style={{ maxWidth: 250 }}
                            value={supplierFilter}
                            onChange={e => setSupplierFilter(e.target.value ? Number(e.target.value) : '')}
                        >
                            <option value="">All suppliers</option>
                            {suppliers.map(s => (
                                <option key={s.id} value={s.id}>
                                    {s.displayName}
                                </option>
                            ))}
                        </Form.Select>
                    </div>
                    {canManageActions && (
                        <Button variant="primary" onClick={() => setGroupModal({ show: true, group: null })}>
                            + Add group
                        </Button>
                    )}
                </div>

                {/* Groups */}
                {filteredGroups.length === 0 && <p className="text-muted text-center py-4">No groups yet.</p>}

                {filteredGroups.map(group => (
                    <div key={group.id} className="mb-3 border rounded p-3">
                        {/* Group header row */}
                        <div className="d-flex justify-content-between align-items-center mb-2">
                            <div>
                                <h5>
                                    {group.name}
                                    {group.nameDe && <span className="text-muted ms-2">({group.nameDe})</span>}
                                    <span className={`badge ms-2 ${group.isActive ? 'text-primary' : 'text-danger'} }`}>
                                        {group.isActive ? 'Active' : 'Inactive'}
                                    </span>
                                    <span className="badge ms-2 text-primary">
                                        {group.isMultiple ? 'Multiple select' : 'Single select'}
                                    </span>
                                    {group.supplier && (
                                        <span className="badge ms-2 text-secondary">{group.supplier.displayName}</span>
                                    )}
                                </h5>
                            </div>
                            {canManageActions && (
                                <DropdownButton
                                    title="Actions"
                                    id={`group-actions-${group.id}`}
                                    variant="outline-primary"
                                >
                                    <Dropdown.Item onClick={() => setGroupModal({ show: true, group: group })}>
                                        Edit
                                    </Dropdown.Item>
                                    <Dropdown.Item onClick={() => setOptionModal({ show: true, groupId: group.id })}>
                                        Add option
                                    </Dropdown.Item>
                                    <Dropdown.Divider />
                                    <Dropdown.Item
                                        className="text-danger"
                                        onClick={() => {
                                            dispatch(
                                                generalSlice.actions.setConfirmModal({
                                                    show: true,
                                                    title: 'Deleting groups',
                                                    contentBody: <div>Are you sure you want to delete this group?</div>,
                                                    action: () => handleDeleteGroup(group.id),
                                                })
                                            );
                                        }}
                                    >
                                        Delete
                                    </Dropdown.Item>
                                </DropdownButton>
                            )}
                        </div>
                        <hr />
                        {/* Options */}
                        {(!group.options || group.options.length === 0) && (
                            <div className="text-center py-3">
                                <p className="text-muted mb-2">No options yet.</p>
                                {canManageActions && (
                                    <Button
                                        variant="outline-primary"
                                        onClick={() => setOptionModal({ show: true, groupId: group.id })}
                                    >
                                        + Create first option
                                    </Button>
                                )}
                            </div>
                        )}
                        {group.options && group.options.length > 0 && (
                            <table className="table table-hover mb-0 mt-2">
                                <thead>
                                    <tr>
                                        <th>Label EN</th>
                                        <th>Label DE</th>
                                        <th>Position</th>
                                        <th className="text-end">Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {group.options.map(opt => (
                                        <tr key={opt.id}>
                                            <td>{opt.label}</td>
                                            <td>{opt.labelDe ?? '-'}</td>
                                            <td>{opt.position ?? '-'}</td>
                                            <td className="text-end">
                                                {canManageActions && (
                                                    <DropdownButton
                                                        title="Actions"
                                                        id={`opt-actions-${opt.id}`}
                                                        variant="outline-primary"
                                                    >
                                                        <Dropdown.Item
                                                            onClick={() =>
                                                                setEditOptionModal({
                                                                    show: true,
                                                                    groupId: group.id,
                                                                    option: opt,
                                                                })
                                                            }
                                                        >
                                                            Edit
                                                        </Dropdown.Item>
                                                        <Dropdown.Divider />
                                                        <Dropdown.Item
                                                            className="text-danger"
                                                            onClick={() => {
                                                                dispatch(
                                                                    generalSlice.actions.setConfirmModal({
                                                                        show: true,
                                                                        title: 'Deleting options',
                                                                        contentBody: (
                                                                            <div>
                                                                                Are you sure you want to delete this
                                                                                option?
                                                                            </div>
                                                                        ),
                                                                        action: () =>
                                                                            handleDeleteOption(group.id, opt.id),
                                                                    })
                                                                );
                                                            }}
                                                        >
                                                            Delete
                                                        </Dropdown.Item>
                                                    </DropdownButton>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                    <tr>
                                        <td>
                                            {canManageActions && (
                                                <Button
                                                    variant="outline-primary"
                                                    onClick={() => setOptionModal({ show: true, groupId: group.id })}
                                                >
                                                    + Add more options
                                                </Button>
                                            )}
                                        </td>
                                        <td colSpan={3} />
                                    </tr>
                                </tbody>
                            </table>
                        )}
                    </div>
                ))}
            </Card>
        </>
    );
}
